<template>
  <div class="round-current">
    <h4
      v-if="isWaitingForOthers && !isPosting"
      class="mb-2"
    >
      warte auf andere Spieler ...
    </h4>
    <BForm
      v-if="currentGuesses != null"
      @submit.prevent="submitGuesses"
    >
      <BFormGroup
        v-for="(phrase, phraseIndex) in currentGuesses"
        :key="`guesses-${phrase.phraseId}`"
        class="mb-2"
      >
        <BFormRadioGroup
          v-model="currentGuessModels[phraseIndex]"
          stacked
        >
          <BFormRadio
            v-for="playerInput in phrase.playerInputs"
            :key="`guesses-${phrase.phraseId}-${playerInput.player}`"
            :value="playerInput.player"
            :disabled="playerInput.isFromPlayer || isPosting || isCurrentGuessesFinished"
          >
            <PhraseWithUserText
              :phrase="phrase.phrase"
              :texts="playerInput.texts"
            />
            <span v-if="isCurrentGuessesFinished"> ({{ resolvePlayerName(playerInput.player) }})</span>
          </BFormRadio>
        </BFormRadioGroup>
      </BFormGroup>
      <BButton
        v-if="!isWaitingForOthers"
        type="submit"
        variant="primary"
        :disabled="!canSubmitGuesses || isPosting || isCurrentGuessesFinished"
      >
        Bestätigen
      </BButton>
    </BForm>
    <BForm
      v-if="currentPhrases != null && currentGuesses == null"
      class="mb-2"
      @submit.prevent="submitTexts"
    >
      <BListGroup class="mb-2">
        <BListGroupItem
          v-for="(phrase, phraseIndex) in currentPhrases"
          :key="`phrase-${phrase.phraseId}`"
        >
          <PhraseWithUserText
            v-if="phrase.texts != null"
            :phrase="phrase.phrase"
            :texts="phrase.texts"
          />
          <span
            v-for="(textPart, partIndex) in phrase.textParts"
            :key="`phrase-${phrase.phraseId}-${partIndex}`"
          >
            <span v-if="textPart.isText">
              {{ textPart.text }}
            </span>
            <!--suppress HtmlFormInputWithoutLabel -->
            <input
              v-else
              v-model="currentTextModels[`${phraseIndex}-${textPart.x}`]"
              class="round-current__text-inputs"
              :class="{
                'round-current__text-inputs--empty': (
                  currentTextModels[`${phraseIndex}-${textPart.x}`] == null
                  || currentTextModels[`${phraseIndex}-${textPart.x}`].length === 0
                ),
              }"
              :placeholder="textPart.text"
              :style="{ width: inputWidth(`${phraseIndex}-${textPart.x}`, textPart.text) }"
              :disabled="isPosting || isCurrentTextsFinished"
            />
          </span>
        </BListGroupItem>
      </BListGroup>
      <BButton
        v-if="!isWaitingForOthers"
        type="submit"
        variant="primary"
        :disabled="!canSubmitTexts || isPosting || isCurrentTextsFinished"
      >
        Bestätigen
      </BButton>
    </BForm>
  </div>
</template>

<script>
import resolvePlayerName from '@/shared/mixins/resolvePlayerName';

import PhraseWithUserText from './PhraseWithUserText.vue';
import { shuffleSeeded } from '../mixins/utilities';

export default {
  name: 'RoundCurrent',
  components: { PhraseWithUserText },
  props: {
    round: {
      type: Object,
      required: true,
    },
    roundNr: {
      type: Number,
      required: true,
    },
    gameHash: {
      type: String,
      required: true,
    },
    players: {
      type: Array,
      required: true,
    },
  },
  mixins: [resolvePlayerName],
  data() {
    return {
      isPosting: false,
      currentTextModels: {},
      currentGuessModels: [],
    };
  },
  computed: {
    isWaitingForOthers() {
      return (
        (this.isCurrentTextsFinished && this.currentGuesses == null)
        || (this.currentGuesses != null && this.isCurrentGuessesFinished)
      );
    },
    // region PHRASES
    currentPhrases() {
      if (this.$store.state.phrases == null) {
        return null;
      }
      if (this.isCurrentTextsFinished) {
        return this.round.phrases.map((currentPhrase) => {
          // noinspection TypeScriptValidateTypes
          const phrase = this.$store.state.phrases[currentPhrase.phraseId];
          const player = this.$store.state.auth.uid;
          const texts = currentPhrase.playerInputs[player].text;
          return {
            phraseId: currentPhrase.phraseId,
            phrase,
            texts,
          };
        });
      }
      return this.round.phrases
        .map((currentPhrase) => {
          // noinspection TypeScriptValidateTypes
          const phrase = this.$store.state.phrases[currentPhrase.phraseId];
          const parts = phrase.split(/%\w*%/g);
          let placeholder = phrase.match(/%\w*%/g) || [];
          placeholder = placeholder.map(text => text.replace(/%/g, ''));
          const textParts = [];
          for (let x = 0; x < placeholder.length; x += 1) {
            textParts.push({
              isText: true,
              text: parts[x],
              x,
            }, {
              isText: false,
              text: placeholder[x],
              x,
            });
          }
          textParts.push({
            isText: true,
            text: parts.slice(-1)[0],
          });
          return {
            phraseId: currentPhrase.phraseId,
            textParts,
          };
        });
    },
    canSubmitTexts() {
      if (this.currentPhrases == null) {
        return false;
      }
      return this.currentPhrases.every((phrase, phraseIndex) => phrase
        .textParts
        .filter(part => !part.isText)
        .every(part => (
          this.currentTextModels[`${phraseIndex}-${part.x}`] != null
          && this.currentTextModels[`${phraseIndex}-${part.x}`].length > 0
        )));
    },
    isCurrentTextsFinished() {
      const player = this.$store.state.auth.uid;
      return this.round.phrases.every(phrase => (
        phrase.playerInputs != null
        && phrase.playerInputs[player] != null
        && phrase.playerInputs[player].text != null
      ));
    },
    isAllPlayersTextsFinished() {
      return this.round.phrases.every((phrase) => {
        if (
          phrase.playerInputs == null
          || Object.values(phrase.playerInputs).length !== this.players.length
        ) {
          return false;
        }
        return Object.values(phrase.playerInputs).every(playerInput => playerInput.text != null);
      });
    },
    // endregion PHRASES
    // region GUESSES
    currentGuesses() {
      if (
        this.$store.state.phrases == null
        || !this.isAllPlayersTextsFinished
      ) {
        return null;
      }
      return this.round.phrases.map((currentPhrase, index) => {
        // noinspection TypeScriptValidateTypes
        const phrase = this.$store.state.phrases[currentPhrase.phraseId];
        const playerInputs = Object.keys(currentPhrase.playerInputs)
          .map(player => ({
            isFromPlayer: player === this.$store.state.auth.uid,
            player,
            texts: currentPhrase.playerInputs[player].text,
          }));
        // each phrase in each round has to be rolled (player - 1) times
        const skipRolls = this.roundNr * (this.players.length - 1) * 3 // previous rounds
          + index * (this.players.length - 1); // rolls for previous rounds
        return {
          phraseId: currentPhrase.phraseId,
          phrase,
          playerInputs: shuffleSeeded(
            playerInputs,
            this.gameHash,
            skipRolls,
          ),
        };
      });
    },
    canSubmitGuesses() {
      if (
        this.currentGuesses == null
        || this.$store.state.auth.uid === this.round.player
      ) {
        return false;
      }
      return this.currentGuessModels.length === this.round.phrases.length;
    },
    isCurrentGuessesFinished() {
      const player = this.$store.state.auth.uid;
      if (player === this.round.player) {
        return true;
      }
      return this.round.phrases
        .every(phrase => (
          phrase.playerInputs != null
          && phrase.playerInputs[player] != null
          && phrase.playerInputs[player].guess != null
        ));
    },
    // endregion GUESSES
  },
  methods: {
    // region PHRASES
    inputWidth(selector, placeholder) {
      if (document.body == null) {
        return 'auto';
      }
      let value = placeholder;
      if (
        this.currentTextModels[selector] != null
        && this.currentTextModels[selector].length > 0
      ) {
        value = this.currentTextModels[selector];
      }
      const span = document.createElement('span');
      span.innerText = value;
      document.body.append(span);
      const { width } = span.getBoundingClientRect();
      span.remove();
      return `${width + 8}px`;
    },
    async submitTexts() {
      this.isPosting = true;
      await this.$store.dispatch('games/setTextsForGame', {
        gameHash: this.gameHash,
        roundNr: this.roundNr,
        currentTextModels: this.currentTextModels,
      });
      this.isPosting = false;
    },
    // endregion PHRASES
    // region GUESSES
    async submitGuesses() {
      this.isPosting = true;
      await this.$store.dispatch('games/setGuessesForGame', {
        gameHash: this.gameHash,
        roundNr: this.roundNr,
        guesses: this.currentGuessModels,
      });
      this.isPosting = false;
    },
    // endregion GUESSES
  },
};
</script>

<style scoped>
  .round-current__text-inputs {
    display: inline-block;
    height: auto;
    width: auto;
    padding: 2px 2px 2px 4px;
    border: none;
    border-radius: 0;
    outline: none;
    font-style: italic;
    text-decoration: underline;
  }

  .round-current__text-inputs--empty:not(:focus) {
    background: black;
  }

  .round-current__text-inputs--empty::placeholder {
    color: white;
    font-style: normal;
    text-decoration: none;
    opacity: 1;
  }
</style>
